import { useCallback, useEffect, useState } from "react";
import { api } from "../api";
import { missingFields, when } from "../format";
import { Link, navigate } from "../router";
import type { DraftSummary, TemplateSummary } from "../types";
import { IconBin, IconChevron, IconDoc, IconDocLines, IconInfo } from "./Icons";

/**
 * Documente: the user's drafts, and the templates to start one from.
 *
 * Two tabs, each its own URL - /documents and /documents/models - so
 * "+ Document nou" is a link like any other. A template starts a draft in
 * one click under the template's name; the editor is where it is renamed,
 * because a title is easier to choose once the document has a shape.
 */
export function Documents({
  tab,
  canEdit = false,
}: {
  tab: "drafts" | "models" | "mine";
  /** Admins may edit the shared templates; the server checks again. */
  canEdit?: boolean;
}) {
  const [templates, setTemplates] = useState<TemplateSummary[]>([]);
  const [drafts, setDrafts] = useState<DraftSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [starting, setStarting] = useState<string | null>(null);
  const [copying, setCopying] = useState<string | null>(null);
  const shared = templates.filter((t) => !t.mine);
  const mine = templates.filter((t) => t.mine);
  // Two-click delete: the bin, then "Ștergeți?". No confirm() dialog.
  const [confirming, setConfirming] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    try {
      const [t, d] = await Promise.all([api.templates(), api.drafts()]);
      setTemplates(t);
      setDrafts(d);
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Eroare");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  async function start(t: TemplateSummary) {
    if (starting) return;
    setStarting(t.slug);
    setError(null);
    try {
      const made = await api.createDraft(t.slug, t.title);
      navigate(`/documents/${made.id}`);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Eroare");
      setStarting(null);
    }
  }

  /** Copy into "Modelele mele" and open it in the editor straight away:
   *  a copy is made to be changed. */
  async function copy(t: TemplateSummary) {
    if (copying) return;
    setCopying(t.slug);
    setError(null);
    try {
      const made = await api.copyTemplate(t.slug);
      navigate(`/documents/models/${encodeURIComponent(made.slug)}`);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Eroare");
      setCopying(null);
    }
  }

  async function removeTemplate(slug: string) {
    setConfirming(null);
    setError(null);
    try {
      await api.deleteTemplate(slug);
      setTemplates((list) => list.filter((t) => t.slug !== slug));
    } catch (e) {
      // Refused while documents use it; the server says so.
      setError(e instanceof Error ? e.message : "Eroare");
    }
  }

  function card(t: TemplateSummary) {
    const edit = t.mine || canEdit;
    return (
      <div className="card" key={t.slug}>
        <span className="fileic" aria-hidden="true"><IconDocLines /></span>
        <h3>{t.title}</h3>
        <p>{t.summary}</p>
        <div className="card-meta">
          {t.basis_label && <span className="art">{t.basis_label}</span>}
          {/* The number that distinguishes this from a template
              site: fields whose statutory basis is known. */}
          {t.sourced_count > 0 && (
            <span className="fineprint">
              {t.sourced_count} din {t.slot_count} câmpuri cu temei citat
            </span>
          )}
          {t.copied_from && <span className="fineprint">copiat din „{t.copied_from}”</span>}
        </div>
        <div className="card-actions">
          <button
            className="btn primary"
            disabled={starting !== null}
            onClick={() => void start(t)}
          >
            {starting === t.slug ? "Se creează…" : "Începe"}
          </button>
          {edit && (
            <Link to={`/documents/models/${encodeURIComponent(t.slug)}`} className="btn">
              Editează
            </Link>
          )}
          {!t.mine && (
            <button className="btn" disabled={copying !== null} onClick={() => void copy(t)}>
              {copying === t.slug ? "Se copiază…" : "Copiază în modelele mele"}
            </button>
          )}
          {t.mine &&
            (confirming === t.slug ? (
              <button className="btn danger" onClick={() => void removeTemplate(t.slug)}>
                Ștergeți?
              </button>
            ) : (
              <button
                className="iconb"
                title="Șterge modelul"
                aria-label="Șterge modelul"
                onClick={() => setConfirming(t.slug)}
              >
                <IconBin size={18} />
              </button>
            ))}
        </div>
      </div>
    );
  }

  async function remove(id: string) {
    setConfirming(null);
    setDrafts((list) => list.filter((d) => d.id !== id));
    try {
      await api.deleteDraft(id);
    } catch {
      // Already gone from the list, which is what the click meant.
      void refresh();
    }
  }

  return (
    <div className="page documents">
      <div className="page-body">
        <div className="docs-inner">
          <div className="docs-head">
            <h1>Documente</h1>
            <Link to="/documents/models" className="btn primary">
              + Document nou
            </Link>
          </div>

          <div className="seg-tabs" role="tablist" aria-label="Documente">
            <button
              role="tab"
              aria-selected={tab === "drafts"}
              className={tab === "drafts" ? "on" : undefined}
              onClick={() => navigate("/documents")}
            >
              Ciornele mele <span className="cnt">{drafts.length}</span>
            </button>
            <button
              role="tab"
              aria-selected={tab === "models"}
              className={tab === "models" ? "on" : undefined}
              onClick={() => navigate("/documents/models")}
            >
              Modele <span className="cnt">{shared.length}</span>
            </button>
            <button
              role="tab"
              aria-selected={tab === "mine"}
              className={tab === "mine" ? "on" : undefined}
              onClick={() => navigate("/documents/mine")}
            >
              Modelele mele <span className="cnt">{mine.length}</span>
            </button>
          </div>

          {error && <p className="error" role="alert">{error}</p>}
          {loading && <p className="muted">se încarcă…</p>}

          {!loading && tab === "drafts" && (
            <>
              {drafts.length === 0 ? (
                <div className="docs-empty">
                  <h3>Niciun document încă</h3>
                  <p className="muted">
                    Alegeți un model și completați câmpurile. Fiecare câmp
                    obligatoriu își citează norma, iar ce lipsește se vede.
                  </p>
                  <Link to="/documents/models" className="btn medium">Vedeți modelele</Link>
                </div>
              ) : (
                <div className="list">
                  {drafts.map((d) => (
                    <div
                      className="item"
                      key={d.id}
                      onMouseLeave={() => confirming === d.id && setConfirming(null)}
                    >
                      <Link to={`/documents/${d.id}`} className="item-link">
                        <span className="fileic" aria-hidden="true"><IconDoc /></span>
                        <span className="item-main">
                          <span className="t">{d.title}</span>
                          <span className="m">
                            {d.template_title} · {when(d.updated_at)}
                          </span>
                        </span>
                        {d.missing_count > 0 ? (
                          <span className="badge warn">{missingFields(d.missing_count)}</span>
                        ) : (
                          <span className="badge ok">complet</span>
                        )}
                        <span className="chev"><IconChevron size={18} /></span>
                      </Link>
                      {confirming === d.id ? (
                        <button className="btn small danger" onClick={() => void remove(d.id)}>
                          Ștergeți?
                        </button>
                      ) : (
                        <button
                          className="iconb"
                          onClick={() => setConfirming(d.id)}
                          aria-label={`Șterge ${d.title}`}
                        >
                          <IconBin size={18} />
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              )}
              <p className="notice">
                <IconInfo />
                Puteți cere un document și din chat: „Fă-mi un contract de
                muncă pentru un vânzător”.
              </p>
            </>
          )}

          {!loading && tab === "models" && (
            <div className="cards">
              {shared.map(card)}
              {shared.length === 0 && <p className="muted">Niciun model disponibil.</p>}
            </div>
          )}

          {!loading && tab === "mine" && (
            <div className="cards">
              {mine.map(card)}
              {mine.length === 0 && (
                <div className="docs-empty">
                  <h3>Niciun model propriu încă</h3>
                  <p className="muted">
                    Copiați un model din „Modele” ca să-l adaptați: formularea
                    și câmpurile copiei le schimbați doar pentru dvs.
                  </p>
                  <Link to="/documents/models" className="btn medium">Vedeți modelele</Link>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
